// client/src/coms/personalisation/LayoutSettings.js
// Settings → Appearance → Layout: presets, panel arrangement (drag between regions, or use the menus),
// sizes, density, text and interface scale, and optional panels. Changes apply as you make them; "Customise
// on screen" opens the live editor with Apply / Cancel / Reset.
import React, { useState } from 'react';
import { usePersonalisation } from './PersonalisationProvider';
import { LAYOUT_PRESET_INFO, PRESET_STRUCTURE } from './layoutModel';
import { DEFAULT_PREFS } from './prefsSchema';
import { Row, Section, Segmented, Slider, Switch } from './settingsControls';

export function PresetSchematic({ structure }) {
  const s = structure || PRESET_STRUCTURE.classic;
  const side = s['sidebar.side'];
  const mside = s['members.side'];
  const cells = [];
  if (s['sidebar.visible'] && side === 'left') cells.push(<span key="sl" className="vx-sch-side" />);
  if (s['members.visible'] && mside === 'left') cells.push(<span key="ml" className="vx-sch-members" />);
  cells.push(
    <span key="main" className="vx-sch-main">
      {s.tabBar === 'always' ? <i className="vx-sch-tabs" /> : null}
      <i className={`vx-sch-lines${s.chatDensity === 'compact' ? ' compact' : ''}`} />
    </span>
  );
  if (s['members.visible'] && mside === 'right') cells.push(<span key="mr" className="vx-sch-members" />);
  if (s['sidebar.visible'] && side === 'right') cells.push(<span key="sr" className="vx-sch-side" />);
  return (
    <span className="vx-sch" aria-hidden="true">
      {cells}
    </span>
  );
}

const REGIONS = [
  { id: 'left', label: 'Left' },
  { id: 'right', label: 'Right' },
  { id: 'hidden', label: 'Hidden' },
];

function regionOf(layout, panel) {
  const p = layout[panel];
  if (!p.visible) return 'hidden';
  return p.side;
}

// Drag "Channels" and "Members" between Left / Right / Hidden; every move is also available from a menu.
export function ArrangementEditor({ layout, onPlace, onUserBar }) {
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const panels = [
    { id: 'sidebar', label: 'Channels' },
    { id: 'members', label: 'Members' },
  ];
  const drop = (region) => {
    if (drag) onPlace(drag, region);
    setDrag(null);
    setOver(null);
  };
  return (
    <div className="vx-arrange" data-testid="layout-arrangement">
      <div className="vx-arrange-regions">
        {REGIONS.map((r) => (
          <div
            key={r.id}
            className={`vx-arrange-region vx-arrange-${r.id}${over === r.id ? ' is-over' : ''}`}
            onDragOver={(e) => {
              if (!drag) return;
              e.preventDefault();
              setOver(r.id);
            }}
            onDragLeave={() => setOver((v) => (v === r.id ? null : v))}
            onDrop={(e) => {
              e.preventDefault();
              drop(r.id);
            }}
            aria-label={`${r.label} region`}
            role="group"
            data-testid={`layout-region-${r.id}`}
          >
            <div className="vx-arrange-region-title">{r.label}</div>
            {r.id === 'left' || r.id === 'right' ? null : <div className="vx-set-hint">Drop here to hide</div>}
            {panels
              .filter((p) => regionOf(layout, p.id) === r.id)
              .map((p) => (
                <div
                  key={p.id}
                  className={`vx-chip${drag === p.id ? ' is-dragging' : ''}`}
                  draggable
                  onDragStart={(e) => {
                    setDrag(p.id);
                    try {
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', p.id);
                    } catch {}
                  }}
                  onDragEnd={() => {
                    setDrag(null);
                    setOver(null);
                  }}
                  data-testid={`layout-chip-${p.id}`}
                >
                  <span aria-hidden="true" className="vx-chip-grip">
                    ⠿
                  </span>
                  <span>{p.label}</span>
                  <label className="vx-visually-hidden" htmlFor={`vx-place-${p.id}`}>
                    Place {p.label}
                  </label>
                  <select id={`vx-place-${p.id}`} className="vx-chip-select" value={r.id} onChange={(e) => onPlace(p.id, e.target.value)} data-testid={`layout-place-${p.id}`}>
                    {REGIONS.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.id === 'hidden' ? 'Hidden' : `On the ${x.label.toLowerCase()}`}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
          </div>
        ))}
        <div className="vx-arrange-chat" aria-hidden="true">
          Conversation
        </div>
      </div>
      <Row label="Your profile and voice controls" hint="Where the user and voice bar sits in the channel list.">
        <Segmented
          label="Profile and voice controls"
          value={layout.userBar}
          options={[
            { value: 'bottom', label: 'Bottom' },
            { value: 'top', label: 'Top' },
          ]}
          onChange={onUserBar}
          testId="layout-userbar"
        />
      </Row>
    </div>
  );
}

export function placePanel(setLayoutValue, panel, region) {
  if (region === 'hidden') setLayoutValue(`${panel}.visible`, false);
  else {
    setLayoutValue(`${panel}.side`, region);
    setLayoutValue(`${panel}.visible`, true);
  }
}

const D = DEFAULT_PREFS.layout;

export default function LayoutSettings({ onCustomiseOnScreen }) {
  const pz = usePersonalisation();
  const layout = pz.globalPrefs.layout;
  const set = pz.setLayoutValue;
  // Restores the arrangement, panels and sizes. Text size and interface scale (Themes & Accessibility) are kept.
  const restoreLayout = () =>
    pz.updateGlobal((p) => ({ ...p, layout: { ...JSON.parse(JSON.stringify(D)), messageScale: p.layout.messageScale, uiScale: p.layout.uiScale } }));

  return (
    <Section
      id="vx-layout"
      title="Layout"
      description="Arrange Voxemera the way you work. Changes apply as you make them and are saved to your account; a server can have its own layout under This Server."
      actions={
        <>
          <button type="button" className="vx-btn small primary" onClick={onCustomiseOnScreen} data-testid="layout-customise-on-screen">
            Customise on screen
          </button>
          <button type="button" className="vx-btn small" onClick={restoreLayout} data-testid="layout-reset">
            Restore layout defaults
          </button>
        </>
      }
    >
      <div id="vx-setting-layout-preset" data-setting="layout-preset" />
      <div id="vx-setting-customise-on-screen" data-setting="customise-on-screen" className="vx-set-hint">
        Customise on screen outlines each area of the real interface so you can move or hide it, with Apply, Cancel and Reset. Hidden panels always come back from the edge handle or from here.
      </div>
      <div className="vx-preset-grid" role="radiogroup" aria-label="Layout preset" data-testid="layout-presets">
        {LAYOUT_PRESET_INFO.map((p) => (
          <button
            key={p.id}
            type="button"
            role="radio"
            aria-checked={layout.preset === p.id}
            className={`vx-preset-card${layout.preset === p.id ? ' is-on' : ''}`}
            onClick={() => pz.setLayoutPreset(p.id)}
            data-testid={`layout-preset-${p.id}`}
            disabled={p.id === 'custom' && layout.preset !== 'custom'}
            title={p.id === 'custom' && layout.preset !== 'custom' ? 'Change any option below to make a custom layout' : undefined}
          >
            <PresetSchematic structure={p.id === 'custom' ? layoutToStructure(layout) : PRESET_STRUCTURE[p.id]} />
            <span className="vx-preset-card-title">{p.label}</span>
            <span className="vx-preset-card-sub">{p.description}</span>
          </button>
        ))}
      </div>

      <h4 className="vx-set-subtitle" id="vx-setting-arrangement" data-setting="arrangement">
        Arrangement
      </h4>
      <ArrangementEditor layout={layout} onPlace={(panel, region) => placePanel(set, panel, region)} onUserBar={(v) => set('userBar', v)} />

      <h4 className="vx-set-subtitle">Size and spacing</h4>
      <Row label="Channel list width" hint="You can also drag the divider beside the channel list." settingId="panel-widths" scope="account" onReset={() => set('sidebar.width', D.sidebar.width)} isDefault={layout.sidebar.width === D.sidebar.width}>
        <Slider label="Channel list width" value={layout.sidebar.width} min={260} max={560} step={10} onChange={(v) => set('sidebar.width', v)} format={(v) => `${v}px`} testId="layout-sidebar-width" />
      </Row>
      <Row label="Member list width" scope="account" onReset={() => set('members.width', D.members.width)} isDefault={layout.members.width === D.members.width}>
        <Slider label="Member list width" value={layout.members.width} min={200} max={420} step={10} onChange={(v) => set('members.width', v)} format={(v) => `${v}px`} testId="layout-members-width" />
      </Row>
      <Row label="Spacing" settingId="spacing" scope="account">
        <Segmented
          label="Spacing"
          value={layout.density}
          options={[
            { value: 'comfortable', label: 'Comfortable' },
            { value: 'compact', label: 'Compact' },
          ]}
          onChange={(v) => set('density', v)}
          testId="layout-density"
        />
      </Row>
      <Row label="Chat density" hint="Compact fits more messages on screen." scope="account">
        <Segmented
          label="Chat density"
          value={layout.chatDensity}
          options={[
            { value: 'cozy', label: 'Cozy' },
            { value: 'compact', label: 'Compact' },
          ]}
          onChange={(v) => set('chatDensity', v)}
          testId="layout-chat-density"
        />
      </Row>
      <div className="vx-set-hint">Text size and interface scale are under Themes & Accessibility.</div>

      <h4 className="vx-set-subtitle">Panels</h4>
      <Row label="Conversation tabs" hint="Automatic shows the tab bar once you have more than one conversation open." settingId="panels" scope="account">
        <Segmented
          label="Conversation tabs"
          value={layout.tabBar}
          options={[
            { value: 'auto', label: 'Automatic' },
            { value: 'always', label: 'Always' },
            { value: 'never', label: 'Never' },
          ]}
          onChange={(v) => set('tabBar', v)}
          testId="layout-tabbar"
        />
      </Row>
      <Row label="Pinned message banner" scope="account">
        <Switch label="Pinned message banner" checked={layout.headerBar} onChange={(v) => set('headerBar', v)} testId="layout-headerbar" />
      </Row>
      <Row label="Activities button" scope="account">
        <Switch label="Activities button" checked={layout.activitiesButton} onChange={(v) => set('activitiesButton', v)} testId="layout-activities-button" />
      </Row>
      <Row label="While an activity is open" hint="Keep the conversation open beside Activities, Canvas and Watch Together." scope="account">
        <Segmented
          label="Activity placement"
          value={layout.activityPlacement}
          options={[
            { value: 'main', label: 'Full width' },
            { value: 'beside-chat', label: 'Beside the chat' },
          ]}
          onChange={(v) => set('activityPlacement', v)}
          testId="layout-activity-placement"
        />
      </Row>
    </Section>
  );
}

export function layoutToStructure(layout) {
  return {
    'sidebar.visible': layout.sidebar.visible,
    'sidebar.side': layout.sidebar.side,
    'members.visible': layout.members.visible,
    'members.side': layout.members.side,
    userBar: layout.userBar,
    tabBar: layout.tabBar,
    headerBar: layout.headerBar,
    activitiesButton: layout.activitiesButton,
    activityPlacement: layout.activityPlacement,
    density: layout.density,
    chatDensity: layout.chatDensity,
  };
}
